/**
 * Component manifests: the prop contract a Twig component declares about
 * itself, in a header comment.
 *
 *   {# @component core.button
 *      @label   components.button.title
 *      @param text:     richtext label=components.button.text required
 *      @param url:      url      label=components.button.url  required
 *      @param bg_color: color    label=components.button.bg    default="{{ theme.colors.brand }}"
 *      @param align:    select   label=components.button.align options="left|center|right" default="center"
 *      @slot  body:              label=components.button.body
 *   #}
 *
 * Why a comment: it is inert at render time, travels inside the fragment's
 * `content` (so no schema column and no API change), and is editable in the
 * existing fragment editor. The parsed result *is* the inspector schema — which
 * is what makes "add a component" a Twig-only change.
 *
 * Labels are `group.key` references into the global `translations` catalog
 * (resolved server-side and returned by GET /template-fragments); a literal
 * label is reported as a warning so `core/*` components cannot ship
 * untranslatable chrome.
 *
 * Parsing never throws. A partner can save a malformed manifest at any time,
 * and the editor must degrade to "parameters inferred" rather than break.
 */

export type ParamKind =
  | 'richtext'
  | 'text'
  | 'url'
  | 'number'
  | 'color'
  | 'image'
  | 'select'
  | 'toggle'
  | 'spacing'
  | 'variable'
  /** Raw HTML/Twig, edited in a code editor rather than a text field. */
  | 'code'
  /**
   * A CSS font-family stack. Its own kind rather than `select`, because the
   * choices are not the component's to declare: they are the web-safe families
   * every mail client has plus whatever the journey theme adds, which only the
   * editor knows. See `emailFonts.ts` for why the list is short.
   */
  | 'font'
  /**
   * Inline CSS declarations, appended to the block's own `style` attribute.
   *
   * The escape hatch for the declaration no param covers — a text-shadow, a
   * `mso-` hint, a letter-spacing on a component that has no control for one.
   * Distinct from `code`, which holds markup: this is a value inside an
   * attribute, so it is never translated, never spell-checked, and appended
   * *last* so it wins over what the component declared.
   */
  | 'css';

const PARAM_KINDS = new Set<ParamKind>([
  'richtext',
  'text',
  'url',
  'number',
  'color',
  'image',
  'select',
  'toggle',
  'spacing',
  'variable',
  'code',
  'font',
  'css',
]);

export const MANIFEST_WARNING = {
  UNKNOWN_KIND: 'unknown-kind',
  LITERAL_LABEL: 'literal-label',
  MALFORMED_DIRECTIVE: 'malformed-directive',
  DUPLICATE_PARAM: 'duplicate-param',
  RESERVED_PARAM: 'reserved-param',
} as const;

/**
 * Prefix reserved for params the compiler injects (`_cvn_id`). A component may
 * *read* them; declaring one would put editor plumbing in the inspector and
 * let a stored prop value shadow the injected one.
 */
export const RESERVED_PARAM_PREFIX = '_cvn_';

export interface ManifestWarning {
  code: string;
  detail: string;
}

/** One declared prop — doubles as the inspector's control descriptor. */
export interface ManifestParam {
  name: string;
  kind: ParamKind;
  /** Translation key (`group.key`) for the control label; null when undeclared. */
  labelKey: string | null;
  required: boolean;
  /**
   * Declared default, verbatim. A `{{ … }}` value is a Twig expression (e.g.
   * `{{ theme.colors.brand }}`) and is emitted unquoted; anything else is a
   * literal.
   */
  default?: string;
  /** `select` choices, in declaration order. */
  options?: string[];
}

/** A slot accepts child blocks, filled via `{% embed %}` + `{% block %}`. */
export interface ManifestSlot {
  name: string;
  labelKey: string | null;
}

export interface ComponentManifest {
  /** Block type from `@component`; null when the directive is absent. */
  type: string | null;
  labelKey: string | null;
  params: ManifestParam[];
  slots: ManifestSlot[];
  /** `@inline` — expand the markup at compile time instead of including it. */
  inline: boolean;
  warnings: ManifestWarning[];
}

/** Any `{# … #}` comment; the manifest is the first one carrying a directive. */
const COMMENT_PATTERN = /\{#([\s\S]*?)#\}/g;

/** A comment counts as the manifest when it carries one of the directives. */
const DIRECTIVE_PATTERN = /@(component|param|slot|label|inline)\b/;

/**
 * Where the manifest comment sits in a source — the discovery rule shared by
 * the parser and by anything that rewrites the comment in place (the visual
 * manifest editor). Null when the source has none.
 */
export function findManifestComment(
  source: string,
): { index: number; length: number; body: string } | null {
  for (const match of source.matchAll(COMMENT_PATTERN)) {
    if (DIRECTIVE_PATTERN.test(match[1])) {
      return {
        index: match.index ?? 0,
        length: match[0].length,
        body: match[1],
      };
    }
  }
  return null;
}

/** `@param name: kind <attrs>` / `@slot name: <attrs>` */
const PARAM_PATTERN = /^@param\s+([\w-]+)\s*:\s*([\w-]+)\s*(.*)$/;
const SLOT_PATTERN = /^@slot\s+([\w-]+)\s*:?\s*(.*)$/;

/** `key="value"` / `key='value'` / `key=value`, plus the bare `required` flag. */
const ATTR_PATTERN =
  /([a-zA-Z_][\w-]*)=(?:"([^"]*)"|'([^']*)'|(\S+))|\b(required)\b/g;

interface ParsedAttrs {
  values: Record<string, string>;
  required: boolean;
}

function parseAttrs(text: string): ParsedAttrs {
  const values: Record<string, string> = {};
  let required = false;

  for (const match of text.matchAll(ATTR_PATTERN)) {
    if (match[5]) {
      required = true;
      continue;
    }
    const [, key, doubleQuoted, singleQuoted, bare] = match;
    values[key] = doubleQuoted ?? singleQuoted ?? bare ?? '';
  }

  return { values, required };
}

/**
 * A label must be a translation key (`group.key`), not display text. Anything
 * containing whitespace is display text someone typed by hand.
 */
function readLabel(
  attrs: ParsedAttrs,
  subject: string,
  warnings: ManifestWarning[],
): string | null {
  const label = attrs.values.label;
  if (label == null || label === '') return null;

  if (/\s/.test(label)) {
    warnings.push({
      code: MANIFEST_WARNING.LITERAL_LABEL,
      detail: `${subject}: label "${label}" is display text, not a translation key`,
    });
  }
  return label;
}

/**
 * The manifest declared by a component's source, or null when the source has no
 * manifest comment at all (partner fragments fall back to inference).
 */
export function parseManifest(source: string): ComponentManifest | null {
  const body = findManifestComment(source)?.body ?? null;
  if (body === null) return null;

  const manifest: ComponentManifest = {
    type: null,
    labelKey: null,
    params: [],
    slots: [],
    inline: false,
    warnings: [],
  };
  const seen = new Set<string>();

  // Directives are line-oriented so a component's contract stays readable in a
  // diff; `@param` values never span lines.
  for (const rawLine of body.split('\n')) {
    const line = rawLine.trim();
    if (!line.startsWith('@')) continue;

    if (line === '@inline' || line.startsWith('@inline ')) {
      manifest.inline = true;
      continue;
    }

    const component = line.match(/^@component\s+(\S+)\s*$/);
    if (component) {
      manifest.type = component[1];
      continue;
    }

    const label = line.match(/^@label\s+(\S+)\s*$/);
    if (label) {
      manifest.labelKey = readLabel(
        { values: { label: label[1] }, required: false },
        '@label',
        manifest.warnings,
      );
      continue;
    }

    const param = line.match(PARAM_PATTERN);
    if (param) {
      const [, name, rawKind, rest] = param;
      const attrs = parseAttrs(rest);

      let kind = rawKind as ParamKind;
      if (!PARAM_KINDS.has(kind)) {
        manifest.warnings.push({
          code: MANIFEST_WARNING.UNKNOWN_KIND,
          detail: `@param ${name}: unknown kind "${rawKind}", treated as text`,
        });
        kind = 'text';
      }

      if (name.startsWith(RESERVED_PARAM_PREFIX)) {
        manifest.warnings.push({
          code: MANIFEST_WARNING.RESERVED_PARAM,
          detail: `@param ${name}: "${RESERVED_PARAM_PREFIX}" is reserved for compiler-injected params; declaration ignored`,
        });
        continue;
      }

      if (seen.has(name)) {
        manifest.warnings.push({
          code: MANIFEST_WARNING.DUPLICATE_PARAM,
          detail: `@param ${name} declared more than once; the last wins`,
        });
        manifest.params = manifest.params.filter((each) => each.name !== name);
      }
      seen.add(name);

      const declared: ManifestParam = {
        name,
        kind,
        labelKey: readLabel(attrs, `@param ${name}`, manifest.warnings),
        required: attrs.required,
      };
      if (attrs.values.default !== undefined) {
        declared.default = attrs.values.default;
      }
      if (attrs.values.options !== undefined) {
        declared.options = attrs.values.options
          .split('|')
          .map((option) => option.trim())
          .filter(Boolean);
      }
      manifest.params.push(declared);
      continue;
    }

    const slot = line.match(SLOT_PATTERN);
    if (slot) {
      const [, name, rest] = slot;
      const attrs = parseAttrs(rest);
      manifest.slots.push({
        name,
        labelKey: readLabel(attrs, `@slot ${name}`, manifest.warnings),
      });
      continue;
    }

    manifest.warnings.push({
      code: MANIFEST_WARNING.MALFORMED_DIRECTIVE,
      detail: `Unrecognized directive: ${line}`,
    });
  }

  return manifest;
}

/**
 * A server-materialized manifest (the `manifest` field on a fragment row) as a
 * ComponentManifest, or null when the shape cannot be trusted.
 *
 * The backend's ManifestParser writes exactly this shape (its tests are this
 * grammar's cases ported 1:1), so this is a guard against version skew and
 * hand-edited rows, not a second parser. Null falls back to `parseManifest`
 * over the fragment's content, exactly as before the field existed.
 */
export function coerceManifest(value: unknown): ComponentManifest | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as ComponentManifest;
  if (!Array.isArray(candidate.params) || !Array.isArray(candidate.slots)) {
    return null;
  }
  return {
    type: typeof candidate.type === 'string' ? candidate.type : null,
    labelKey: typeof candidate.labelKey === 'string' ? candidate.labelKey : null,
    params: candidate.params,
    slots: candidate.slots,
    inline: candidate.inline === true,
    warnings: Array.isArray(candidate.warnings) ? candidate.warnings : [],
  };
}

/**
 * True when the value is **exactly one** Twig expression rather than a literal.
 *
 * The interior must not contain `}}`: without that, `{{ a }} and {{ b }}` starts with
 * `{{` and ends with `}}` and was read as a single expression, so it was unwrapped to
 * the nonsense `a }} and {{ b`. Found by testing interpolation, and wrong for a
 * manifest default in the same way.
 */
export function isExpressionValue(value: string): boolean {
  return /^\{\{(?:(?!\}\})[\s\S])*\}\}$/.test(value.trim());
}

/** `{{ theme.colors.brand }}` → `theme.colors.brand`. */
export function unwrapExpression(value: string): string {
  return value.trim().replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '');
}
